import React, { Suspense, useEffect, useRef, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Loader, OrthographicCamera } from '@react-three/drei';
import { ArrowUp, Mic, Settings, Square } from 'lucide-react';
import ReactAudioPlayer from 'react-audio-player';
import * as THREE from 'three';


import Avatar from '../components/Avatar';
import LipSyncController from '../components/LipSyncController';
import HeadAnimator from '../components/HeadAnimator';
import SceneBackground from '../components/SceneBackground';
import SceneLighting from '../components/SceneLighting';

import { loadSceneConfig } from '../config/sceneDefaults';
import { makeSpeech, streamOpenAIResponse } from '../utils/streamingSpeech';

function MixerUpdater({ mixer }) {
  useFrame((state, delta) => {
    if (mixer) {
      mixer.update(delta);
    }
  });
  return null;
}

function AvatarPage() {
  const audioPlayer = useRef();

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 0.4; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.2); }
      }

      .send-button {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
      }

      .send-button:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.95) !important;
        transform: scale(1.05);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25) !important;
      }

      .send-button:active:not(:disabled) {
        transform: scale(0.95);
      }

      .send-button::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%, rgba(255, 255, 255, 0.1) 100%);
        border-radius: 50%;
        pointer-events: none;
      }

      @keyframes mic-pulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
        50% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
      }

      .mic-button.listening {
        animation: mic-pulse 1.5s infinite;
        background: rgba(239, 68, 68, 0.9) !important;
        color: white !important;
        border-color: rgba(239, 68, 68, 0.8) !important;
      }

      .admin-link {
        position: absolute;
        top: 16px;
        right: 16px;
        z-index: 1000;
        color: rgba(255,255,255,0.5);
        text-decoration: none;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 4px;
        transition: color 0.2s;
      }
      .admin-link:hover {
        color: rgba(255,255,255,0.9);
      }

      .chat-history-panel {
        position: fixed;
        right: 20px;
        bottom: 100px;
        width: 380px;
        max-height: 35vh;
        overflow-y: auto;
        z-index: 500;
        padding: 10px;
        background: #33333354;
        border-radius: 8px;
      }

      .chat-history-panel::-webkit-scrollbar {
        width: 6px;
      }
      .chat-history-panel::-webkit-scrollbar-track {
        background: transparent;
      }
      .chat-history-panel::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.2);
        border-radius: 3px;
      }

      @media (max-width: 768px) {
        .chat-history-panel {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [speak, setSpeak] = useState(false);
  const [text, setText] = useState("");
  const [audioSource, setAudioSource] = useState(null);
  const [currentSpeechText, setCurrentSpeechText] = useState('');
  const [playing, setPlaying] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [isAiResponding, setIsAiResponding] = useState(false);
  const [, setMorphTargetDictionaryBody] = useState(null);
  const [, setMorphTargetDictionaryLowerTeeth] = useState(null);
  const [avatarRef, setAvatarRef] = useState(null);
  const [mixer, setMixer] = useState(null);
  const [, setAudioDuration] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [sceneConfig] = useState(() => loadSceneConfig());
  const responseSessionRef = useRef(0);
  const activeAudioRef = useRef(null);
  const playbackQueueRef = useRef([]);
  const ttsQueueRef = useRef([]);
  const isSynthesizingRef = useRef(false);
  const isPlaybackActiveRef = useRef(false);
  const streamFinishedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const currentAssistantIdRef = useRef(null);

  const STYLES = {
    chatContainer: {
      position: 'absolute',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '90%',
      maxWidth: '800px',
      zIndex: 500
    },
    chatHistory: {
      maxHeight: '400px',
      overflowY: 'auto',
      marginBottom: '20px',
      padding: '20px 10px'
    },
    messageWrapper: {
      marginBottom: '16px',
      display: 'flex',
      width: '100%'
    },
    userMessageWrapper: {
      justifyContent: 'flex-end'
    },
    aiMessageWrapper: {
      justifyContent: 'flex-start'
    },
    userMessage: {
      background: 'transparent',
      color: '#f3f4f6',
      padding: '12px 16px',
      borderRadius: '16px',
      maxWidth: '70%',
      border: '1px solid rgba(113, 113, 122, 0.4)',
      backdropFilter: 'blur(10px)',
      fontSize: '14px',
      lineHeight: '1.4',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.7)'
    },
    aiMessage: {
      background: 'rgba(255, 255, 255, 0.08)',
      color: '#e5e7eb',
      padding: '12px 16px',
      borderRadius: '16px',
      maxWidth: '70%',
      border: '1px solid rgba(113, 113, 122, 0.4)',
      backdropFilter: 'blur(15px)',
      fontSize: '14px',
      lineHeight: '1.4',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      textShadow: '0 1px 13px rgba(255, 255, 255, 0.8)'
    },
    loadingMessage: {
      background: 'rgba(255, 255, 255, 0.06)',
      color: '#6b7280',
      padding: '12px 16px',
      borderRadius: '16px',
      maxWidth: '70%',
      border: '1px solid rgba(113, 113, 122, 0.3)',
      backdropFilter: 'blur(15px)',
      fontSize: '14px',
      lineHeight: '1.4',
      fontStyle: 'italic',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
      textShadow: '0 1px 2px rgba(255, 255, 255, 0.6)'
    },
    inputWrapper: {
      background: 'rgba(255, 255, 255, 0.08)',
      borderRadius: '24px',
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      border: '1px solid rgba(113, 113, 122, 0.3)',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
    },
    textInput: {
      flex: 1,
      background: 'transparent',
      border: 'none',
      outline: 'none',
      color: '#ffffff',
      fontSize: '16px',
      resize: 'none',
      fontFamily: 'inherit'
    },
    sendButton: {
      background: speak ?
        'rgba(255, 255, 255, 0.3)' :
        'rgba(255, 255, 255, 0.9)',
      border: speak ?
        '1px solid rgba(113, 113, 122, 0.4)' :
        '1px solid rgba(255, 255, 255, 0.8)',
      borderRadius: '50%',
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: speak ? 'not-allowed' : 'pointer',
      color: speak ? '#9ca3af' : '#000000',
      opacity: 1,
      backdropFilter: 'blur(15px)',
      boxShadow: speak ?
        '0 2px 8px rgba(0, 0, 0, 0.1)' :
        '0 4px 16px rgba(0, 0, 0, 0.2)'
    },
    micButton: {
      background: isListening ?
        'rgba(239, 68, 68, 0.9)' :
        'rgba(255, 255, 255, 0.15)',
      border: isListening ?
        '1px solid rgba(239, 68, 68, 0.8)' :
        '1px solid rgba(113, 113, 122, 0.4)',
      borderRadius: '50%',
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: isListening ? '#ffffff' : '#d1d5db',
      opacity: 1,
      backdropFilter: 'blur(15px)',
      boxShadow: isListening ?
        '0 4px 16px rgba(239, 68, 68, 0.3)' :
        '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    loadingDots: {
      display: 'inline-flex',
      gap: '2px'
    },
    loadingDot: {
      width: '4px',
      height: '4px',
      borderRadius: '50%',
      backgroundColor: '#9ca3af',
      animation: 'pulse 1.5s ease-in-out infinite'
    }
  };

  useEffect(() => {
    if (avatarRef && !mixer) {
      const newMixer = new THREE.AnimationMixer(avatarRef);
      setMixer(newMixer);
    }
  }, [avatarRef, mixer]);


  function resetMouthMorphTargets() {
    if (!avatarRef) return;

    avatarRef.traverse(node => {
      if (!node.morphTargetInfluences || !node.morphTargetDictionary) return;
      const dict = node.morphTargetDictionary;
      const inf = node.morphTargetInfluences;
      [
        'jawOpen',
        'mouthClose',
        'mouthFunnel',
        'mouthPucker',
        'mouthSmile_L',
        'mouthSmile_R',
        'mouthFrown_L',
        'mouthFrown_R',
        'mouthStretch_L',
        'mouthStretch_R',
        'mouthUpperUp_L',
        'mouthUpperUp_R',
        'mouthLowerDown_L',
        'mouthLowerDown_R',
        'mouthPress_L',
        'mouthPress_R',
        'mouthRollLower',
        'mouthRollUpper',
        'cheekSquint_L',
        'cheekSquint_R',
        'cheekPuff',
        'noseSneer_L',
        'noseSneer_R',
        'browInnerUp',
        'browDown_L',
        'browDown_R',
        'browOuterUp_L',
        'browOuterUp_R',
        'tongue_out',
      ].forEach(key => {
        if (dict[key] !== undefined) inf[dict[key]] = 0;
      });
    });
  }

  function cleanupAudioUrl(url) {
    if (typeof url === 'string' && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  function upsertAssistantMessage(content, sessionId) {
    if (sessionId !== responseSessionRef.current) return;
    const assistantId = `assistant-${sessionId}`;
    currentAssistantIdRef.current = assistantId;
    setIsAiResponding(false);

    setChatHistory(prev => {
      const index = prev.findIndex(message => message.id === assistantId);
      if (index === -1) {
        return [...prev, { id: assistantId, role: 'assistant', content }];
      }

      const next = [...prev];
      next[index] = { ...next[index], content };
      return next;
    });
  }

  function finishIfIdle(sessionId = responseSessionRef.current) {
    if (sessionId !== responseSessionRef.current) return;

    const busy =
      !streamFinishedRef.current ||
      isSynthesizingRef.current ||
      isPlaybackActiveRef.current ||
      ttsQueueRef.current.length > 0 ||
      playbackQueueRef.current.length > 0;

    if (busy) return;

    setSpeak(false);
    setPlaying(false);
    setIsAiResponding(false);
    setCurrentSpeechText('');
  }

  function playNextAudio(sessionId = responseSessionRef.current) {
    if (sessionId !== responseSessionRef.current || isPlaybackActiveRef.current) return;

    const next = playbackQueueRef.current.shift();
    if (!next) {
      finishIfIdle(sessionId);
      return;
    }

    if (next.sessionId !== responseSessionRef.current) {
      cleanupAudioUrl(next.url);
      playNextAudio(sessionId);
      return;
    }

    activeAudioRef.current = next;
    isPlaybackActiveRef.current = true;
    setCurrentSpeechText(next.text);
    setAudioSource(next.url);
    setSpeak(true);
  }

  async function processTtsQueue(sessionId = responseSessionRef.current) {
    if (isSynthesizingRef.current) return;
    isSynthesizingRef.current = true;

    while (ttsQueueRef.current.length > 0) {
      const job = ttsQueueRef.current.shift();
      if (!job || job.sessionId !== responseSessionRef.current) continue;

      try {
        const speechResponse = await makeSpeech(job.text, {
          signal: abortControllerRef.current?.signal,
        });
        const { filename } = speechResponse.data;

        if (job.sessionId !== responseSessionRef.current) {
          cleanupAudioUrl(filename);
          continue;
        }

        playbackQueueRef.current.push({
          sessionId: job.sessionId,
          text: job.text,
          url: filename,
        });
        playNextAudio(job.sessionId);
      } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') continue;
        console.error('TTS chunk error:', error);
      }
    }

    isSynthesizingRef.current = false;
    finishIfIdle(sessionId);
  }

  function enqueueTtsChunk(chunk, sessionId) {
    const textChunk = chunk.trim();
    if (!textChunk || sessionId !== responseSessionRef.current) return;

    ttsQueueRef.current.push({ sessionId, text: textChunk });
    processTtsQueue(sessionId);
  }

  function clearSpeechQueues({ abort = true } = {}) {
    if (abort && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    ttsQueueRef.current = [];
    playbackQueueRef.current.forEach(item => cleanupAudioUrl(item.url));
    playbackQueueRef.current = [];
    isSynthesizingRef.current = false;
    isPlaybackActiveRef.current = false;
    streamFinishedRef.current = true;

    const audio = audioPlayer.current?.audioEl?.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    if (activeAudioRef.current?.url) {
      cleanupAudioUrl(activeAudioRef.current.url);
    }
    activeAudioRef.current = null;

    setAudioSource(null);
    setCurrentSpeechText('');
    resetMouthMorphTargets();
  }

  function playerEnded(e) {
    const finished = activeAudioRef.current;
    if (finished?.url) cleanupAudioUrl(finished.url);

    activeAudioRef.current = null;
    isPlaybackActiveRef.current = false;
    setAudioSource(null);
    setPlaying(false);
    setCurrentSpeechText('');
    resetMouthMorphTargets();

    if (window.blinkAction && mixer) {
      setTimeout(() => {
        window.blinkAction.reset();
        window.blinkAction.play();
      }, 100);
    }

    requestAnimationFrame(() => playNextAudio(finished?.sessionId || responseSessionRef.current));
  }

  function playerReady(e) {
    const currentAudio = activeAudioRef.current;
    if (!currentAudio || currentAudio.ready) return;

    const audio = audioPlayer.current?.audioEl?.current;
    if (!audio) return;

    currentAudio.ready = true;
    setAudioDuration(audio.duration || 0);

    audio.play()
      .then(() => {
        setPlaying(true);
      })
      .catch(error => {
        console.warn('Audio play failed:', error);
        cleanupAudioUrl(currentAudio.url);
        activeAudioRef.current = null;
        isPlaybackActiveRef.current = false;
        setAudioSource(null);
        setPlaying(false);
        requestAnimationFrame(() => playNextAudio(currentAudio.sessionId));
      });
  }

  const handleSendMessage = async () => {
    if (!text.trim() || speak) return;

    const userMessage = text.trim();
    const sessionId = responseSessionRef.current + 1;
    responseSessionRef.current = sessionId;
    currentAssistantIdRef.current = `assistant-${sessionId}`;
    streamFinishedRef.current = false;

    clearSpeechQueues({ abort: true });
    streamFinishedRef.current = false;
    abortControllerRef.current = new AbortController();

    setText('');
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsAiResponding(true);
    setSpeak(true);

    try {
      const finalText = await streamOpenAIResponse(userMessage, {
        signal: abortControllerRef.current.signal,
        onDelta: partialText => upsertAssistantMessage(partialText, sessionId),
        onTtsChunk: chunk => enqueueTtsChunk(chunk, sessionId),
      });

      streamFinishedRef.current = true;
      if (!finalText) {
        upsertAssistantMessage('Nao consegui gerar uma resposta agora.', sessionId);
      }
      finishIfIdle(sessionId);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Streaming response error:', error);
      streamFinishedRef.current = true;
      setIsAiResponding(false);
      setSpeak(false);
      upsertAssistantMessage('Desculpe, encontrei um erro ao processar sua solicitacao.', sessionId);
    }
  };

  const handleMicClick = () => {
    if (isListening) {
      if (window.recognition) {
        window.recognition.stop();
      }
      setIsListening(false);
      return;
    }

    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Seu navegador não suporta reconhecimento de voz. Use Chrome ou Edge.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.lang = 'pt-BR';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    window.recognition = recognition;

    recognition.onstart = () => {
      setIsListening(true);
      setText('');
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        setText(finalTranscript);
      } else if (interimTranscript) {
        setText(interimTranscript);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        alert('Permissão de microfone negada. Verifique as configurações do navegador.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      if (text.trim() && !speak) {
        handleSendMessage();
      }
    };

    recognition.start();
  };

  const handleStop = () => {
    responseSessionRef.current += 1;
    clearSpeechQueues({ abort: true });
    setSpeak(false);
    setPlaying(false);
    setIsAiResponding(false);
  };

  return (
    <div className="full" style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
      {/* Admin link */}
      <a href="/admin" className="admin-link">
        <Settings size={14} />
        Admin
      </a>

      {/* Stop button - appears when speaking */}
      {playing && (
        <button
          onClick={handleStop}
          style={{
            position: 'absolute',
            top: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 600,
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(239, 68, 68, 0.9)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.2s'
          }}
          title="Parar fala"
        >
          <Square size={18} fill="white" />
        </button>
      )}

      {/* Chat history panel - right side, desktop only */}
      {(chatHistory.length > 0 || isAiResponding) && (
        <div className="chat-history-panel">
          <div style={{ ...STYLES.chatHistory, marginBottom: 0 }}>
            {chatHistory.map((message, index) => (
              <div
                key={index}
                style={{
                  ...STYLES.messageWrapper,
                  ...(message.role === 'user' ? STYLES.userMessageWrapper : STYLES.aiMessageWrapper)
                }}
              >
                <div style={message.role === 'user' ? STYLES.userMessage : STYLES.aiMessage}>
                  {message.content}
                </div>
              </div>
            ))}
            {isAiResponding && (
              <div style={{ ...STYLES.messageWrapper, ...STYLES.aiMessageWrapper }}>
                <div style={STYLES.loadingMessage}>
                  <span>Gerando resposta</span>
                  <div style={STYLES.loadingDots}>
                    <div style={{ ...STYLES.loadingDot, animationDelay: '0s' }}></div>
                    <div style={{ ...STYLES.loadingDot, animationDelay: '0.2s' }}></div>
                    <div style={{ ...STYLES.loadingDot, animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Input - center bottom */}
      <div style={STYLES.chatContainer}>
        <div style={STYLES.inputWrapper}>
          <input
            type="text"
            style={STYLES.textInput}
            value={text}
            onChange={(e) => setText(e.target.value.substring(0, 200))}
            placeholder="Digite ou fale algo..."
            disabled={speak || isListening}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          />
          <button
            onClick={handleMicClick}
            style={STYLES.micButton}
            className={`mic-button ${isListening ? 'listening' : ''}`}
            disabled={speak}
            title={isListening ? 'Parar de ouvir' : 'Falar com o microfone'}
          >
            <Mic size={20} strokeWidth={2.5} />
          </button>
          <button
            onClick={handleSendMessage}
            style={STYLES.sendButton}
            className="send-button"
            disabled={speak || !text.trim()}
          >
            <ArrowUp size={20} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <ReactAudioPlayer
        src={audioSource}
        ref={audioPlayer}
        onEnded={playerEnded}
        onCanPlay={playerReady}
        onCanPlayThrough={playerReady}
        onLoadedMetadata={(e) => {
          const duration = e.target.duration;
          console.log('Audio metadata loaded, duration:', duration);
          setAudioDuration(duration);
        }}
      />

      <Canvas dpr={2} onCreated={(ctx) => {
        ctx.gl.physicallyCorrectLights = true;
      }}>
        <OrthographicCamera
          makeDefault
          zoom={2000}
          position={[0, 1.65, 1]}
        />

        <Suspense fallback={null}>
          <SceneBackground config={sceneConfig.background} />
        </Suspense>
        <SceneLighting config={sceneConfig.lighting} />

        <Suspense fallback={null}>
          <Avatar
            avatar_url={sceneConfig.avatar?.url || '/model.glb'}
            setMorphTargetDictionaryBody={setMorphTargetDictionaryBody}
            setMorphTargetDictionaryLowerTeeth={setMorphTargetDictionaryLowerTeeth}
            materialConfig={sceneConfig.materials}
            setAvatarRef={setAvatarRef}
            mixer={mixer}
          />
        </Suspense>

        <MixerUpdater mixer={mixer} />

        <LipSyncController
          audioPlayerRef={audioPlayer}
          avatarRef={avatarRef}
          playing={playing}
          speechText={currentSpeechText}
        />

        <HeadAnimator avatarRef={avatarRef} playing={playing} animationConfig={sceneConfig.animation} />

      </Canvas>
      <Loader dataInterpolation={(p) => `Loading... please wait`} />
    </div>
  );
}

export default AvatarPage;
